import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Button,
  Grid,
  Stack,
  Box,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import InputAdornment from "@mui/material/InputAdornment";
import {
  Delete as DeleteIcon,
  FileCopy as CloneIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { INDIGO, SLATE } from "../../constants/Colors";
import useApiCall from "../../hooks/useApiCall";
import CloneTemplateDialog from "./components/CloneTemplateDialog";
import { usePageMeta } from "../../contexts/PageMetaContext";
import { AGENT_PROFILE_TYPES } from "../../constants/Constants";
import LoadingSpinner from "../../components/LoadingSpinner";

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900] },
};

// Category definitions
const CATEGORY_FILTERS = [
  { label: "All", value: "all" },
  { label: "Higher Education", value: "higher_ed" },
  { label: "E-Commerce", value: "ecommerce" },
  { label: "Other", value: "other" },
];

// Derived from AGENT_PROFILE_TYPES so the category grouping cannot drift out of
// sync with the agent types themselves.
const HIGHER_ED_TYPES = new Set(
  AGENT_PROFILE_TYPES.filter((t) => t.categories?.includes("higher_ed")).map((t) => t.value)
);
const ECOMMERCE_TYPES = new Set(
  AGENT_PROFILE_TYPES.filter((t) => t.categories?.includes("ecommerce")).map((t) => t.value)
);

const getCategoryForType = (agentType) => {
  if (HIGHER_ED_TYPES.has(agentType)) return "higher_ed";
  if (ECOMMERCE_TYPES.has(agentType)) return "ecommerce";
  return "other";
};

const SECTION_META = {
  higher_ed: { label: "Higher Education", color: "#0071e3", bg: "rgba(0,113,227,0.1)" },
  ecommerce:  { label: "E-Commerce",       color: "#7c3aed", bg: "rgba(124,58,237,0.1)" },
  other:      { label: "Other",            color: "#6e6e73", bg: "rgba(110,110,115,0.1)" },
};

const getProfileType = (agentType) =>
  AGENT_PROFILE_TYPES.find((p) => p.value === agentType) || {
    emoji: "⚙️",
    label: agentType || "Agent",
    description: "Configure this agent template.",
  };

export default function AgentTemplatesList() {
  const navigate = useNavigate();
  const tokens = useSelector((state) => state?.tokens.value);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setPageMeta({
      description: "Browse and configure pre-built agent templates. Clone them for your clients to get started quickly.",
      actions: (
        <Button
          variant="contained"
          startIcon={<AddIcon sx={{ fontSize: "1rem" }} />}
          onClick={() => navigate("/admin/agent-templates/new")}
          sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", "&:hover": { boxShadow: "none" } }}
        >
          Create Template
        </Button>
      ),
    });
    return () => setPageMeta({});
  }, [setPageMeta]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await makeRequest("/v2/agent_templates", {
        method: "GET",
        authTokens: tokens,
      });
      setTemplates(response.templates || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (template, event) => {
    event.stopPropagation();
    if (template.instance_count > 0) return;
    setDeletingTemplate(template);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingTemplate) return;
    setDeleting(true);
    try {
      await makeRequest(`/v2/agent_templates/${deletingTemplate.id}`, {
        method: "DELETE",
        authTokens: tokens,
      });
      setDeletingTemplate(null);
      fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
    } finally {
      setDeleting(false);
    }
  };

  const handleClone = (template, event) => {
    event.stopPropagation();
    setSelectedTemplate(template);
    setCloneDialogOpen(true);
  };

  const handleCloneSuccess = () => {
    setCloneDialogOpen(false);
    setSelectedTemplate(null);
    fetchTemplates();
  };

  // Client-side filtering + grouping
  const grouped = useMemo(() => {
    const query = search.toLowerCase().trim();
    const filtered = templates.filter((t) => {
      if (query && !t.name.toLowerCase().includes(query)) return false;
      if (activeCategory !== "all" && getCategoryForType(t.agent_type) !== activeCategory) return false;
      return true;
    });

    const groups = { higher_ed: [], ecommerce: [], other: [] };
    filtered.forEach((t) => {
      const cat = getCategoryForType(t.agent_type);
      groups[cat].push(t);
    });
    return groups;
  }, [templates, search, activeCategory]);

  const visibleCategories = activeCategory === "all"
    ? ["higher_ed", "ecommerce", "other"]
    : [activeCategory];

  const totalVisible = visibleCategories.reduce((sum, c) => sum + grouped[c].length, 0);

  return (
    <>
      {/* Search + Filter Pills */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 3, flexWrap: "wrap", gap: 1.5, width: "100%" }}>
        <TextField
          size="small"
          placeholder="Search agents..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ ...INPUT_SX, minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: "1rem", color: SLATE[400] }} />
              </InputAdornment>
            ),
          }}
        />

        <Stack direction="row" spacing={1}>
          {CATEGORY_FILTERS.map((cat) => {
            const active = activeCategory === cat.value;
            return (
              <Box
                key={cat.value}
                component="button"
                onClick={() => setActiveCategory(cat.value)}
                sx={{
                  border: "1px solid",
                  borderColor: active ? "#1e293b" : "rgba(0,0,0,0.12)",
                  borderRadius: "980px",
                  bgcolor: active ? "#1e293b" : "transparent",
                  color: active ? "#ffffff" : "#6e6e73",
                  px: "14px",
                  py: "6px",
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  fontFamily: "inherit",
                  "&:hover": {
                    borderColor: "#1e293b",
                    color: active ? "#ffffff" : "#1e293b",
                    bgcolor: active ? "#1e293b" : "rgba(30,41,59,0.06)",
                  },
                }}
              >
                {cat.label}
              </Box>
            );
          })}
        </Stack>
      </Stack>

      {/* Loading */}
      {loading && (
        <LoadingSpinner message="Loading Agent Stores." subMessage="Please wait while we fetch your agent stores" />
      )}

      {/* Sections */}
      {!loading && (
        <Stack spacing={4} sx={{ width: "100%" }}>
          {totalVisible === 0 && (
            <Box sx={{ textAlign: "center", my: 6 }}>
              <Typography sx={{ color: "#6e6e73", fontSize: "0.9375rem" }}>
                No templates found
              </Typography>
            </Box>
          )}

          {visibleCategories.map((cat) => {
            const items = grouped[cat];
            if (items.length === 0) return null;
            const meta = SECTION_META[cat];

            return (
              <Box key={cat}>
                {/* Section Header */}
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: meta.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#6e6e73", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                    {meta.label}
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: "rgba(0,0,0,0.06)",
                      borderRadius: "980px",
                      px: "8px",
                      py: "1px",
                      fontSize: "0.6875rem",
                      fontWeight: 600,
                      color: "#6e6e73",
                    }}
                  >
                    {items.length} {items.length === 1 ? "template" : "templates"}
                  </Box>
                </Stack>

                {/* Cards Grid */}
                <Grid container spacing={2.5}>
                  {items.map((template) => (
                    <Grid item key={template.id} xs={12} sm={6} md={4} lg={3}>
                      <TemplateCard
                        template={template}
                        categoryMeta={meta}
                        onEdit={() => navigate(`/admin/agent-templates/${template.id}/edit`)}
                        onClone={(e) => handleClone(template, e)}
                        onDelete={(e) => handleDeleteClick(template, e)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          })}
        </Stack>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingTemplate} onClose={() => !deleting && setDeletingTemplate(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>Delete Template</Typography>
          <IconButton size="small" onClick={() => setDeletingTemplate(null)} disabled={deleting} sx={{ color: SLATE[400] }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Typography sx={{ fontSize: "0.875rem", color: SLATE[600], mb: 3 }}>
            Are you sure you want to delete <strong>{deletingTemplate?.name}</strong>? This action cannot be undone.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              onClick={() => setDeletingTemplate(null)}
              disabled={deleting}
              variant="outlined"
              size="small"
              sx={{ borderColor: SLATE[300], color: SLATE[700], textTransform: "none", "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] } }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleting}
              variant="contained"
              size="small"
              sx={{ bgcolor: "#ef4444", textTransform: "none", boxShadow: "none", "&:hover": { bgcolor: "#dc2626", boxShadow: "none" } }}
            >
              {deleting && <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} />}
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <CloneTemplateDialog
        open={cloneDialogOpen}
        template={selectedTemplate}
        onClose={() => { setCloneDialogOpen(false); setSelectedTemplate(null); }}
        onSuccess={handleCloneSuccess}
      />
    </>
  );
}

function TemplateCard({ template, categoryMeta, onEdit, onClone, onDelete }) {
  const profile = getProfileType(template.agent_type);
  const instanceCount = template.instance_count || 0;

  return (
    <Box
      sx={{
        border: "1px solid rgba(0,0,0,0.07)",
        borderRadius: "16px",
        bgcolor: "#ffffff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
        p: "20px",
        gap: "12px",
        transition: "box-shadow 0.2s ease",
        "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.09)" },
      }}
    >
      {/* Top row: icon + category pill + delete */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "16px",
            bgcolor: categoryMeta.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.75rem",
            flexShrink: 0,
          }}
        >
          {profile.emoji}
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              bgcolor: "rgba(0,0,0,0.06)",
              color: "#6e6e73",
              borderRadius: "980px",
              px: "10px",
              py: "4px",
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            {categoryMeta.label}
          </Box>
          <IconButton
            size="small"
            title={instanceCount > 0 ? `Cannot delete — ${instanceCount} active instance${instanceCount > 1 ? "s" : ""}` : "Delete"}
            onClick={onDelete}
            disabled={instanceCount > 0}
            sx={{ color: "#9ca3af", "&:hover": { color: "#ef4444", bgcolor: "rgba(239,68,68,0.06)" }, "&.Mui-disabled": { color: "#d1d5db" } }}
          >
            <DeleteIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Stack>
      </Stack>

      {/* Name */}
      <Typography sx={{ fontSize: "1.0625rem", fontWeight: 700, color: "#1d1d1f", lineHeight: 1.3 }}>
        {template.name}
      </Typography>

      {/* Description */}
      <Typography
        sx={{
          fontSize: "0.875rem",
          color: "#6e6e73",
          lineHeight: 1.55,
          display: "-webkit-box",
          WebkitLineClamp: 3,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          flex: 1,
        }}
      >
        {profile.description}
      </Typography>

      {/* Instance count */}
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Typography sx={{ fontSize: "0.875rem", color: "#10b981", fontWeight: 600, lineHeight: 1 }}>↗</Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: "#374151", fontWeight: 500 }}>
          {instanceCount} {instanceCount === 1 ? "active instance" : "active instances"}
        </Typography>
      </Stack>

      {/* Action buttons */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton
          size="small"
          title="Clone for Client"
          onClick={onClone}
          sx={{
            border: "1px solid rgba(0,0,0,0.12)",
            borderRadius: "10px",
            color: "#6b7280",
            "&:hover": { bgcolor: "rgba(0,0,0,0.04)", color: "#374151" },
          }}
        >
          <CloneIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
        <Button
          variant="contained"
          onClick={onEdit}
          sx={{
            flex: 1,
            fontSize: "0.875rem",
            fontWeight: 500,
            textTransform: "none",
            borderRadius: "10px",
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          Configure
        </Button>
      </Stack>
    </Box>
  );
}
